import './index.css'

import cn from 'clsx'
import * as React from 'react'

import Button from 'components/ui/button'
import Icon, { IconKey } from 'components/ui/icon'

type Props = React.ComponentPropsWithoutRef<'input'> & {
  icon?: IconKey
  disabled?: boolean
  className?: string
  invalid?: boolean
  inputRef?:
    | React.RefCallback<HTMLInputElement>
    | React.MutableRefObject<HTMLInputElement | null>
  before?: React.ReactNode
  onClear?: (...args: Array<any>) => any
  autoFocus?: boolean
  fullWidth?: boolean
  defaultValue?: any
  value?: string
}
/**
 * @deprecated This component should be extracted to components/ds following the SearchTextField/PasswordTextField pattern.
 * @see {@link components/ds/TextField}
 * @see {@link components/ds/SearchTextField}
 * @see {@link components/ds/PasswordTextField}
 * @migration
 * - This component provides useful input functionality (icon, clear button, before slot, invalid state)
 * - Instead of asking consumers to manually compose TextField with slots, extract this to a reusable DS component
 * - Suggested approach: Create `FormTextField` in components/ds that wraps TextField with these features:
 *   - icon prop → Renders icon in `<TextField.Slot side="left">`
 *   - before prop → Renders content in `<TextField.Slot side="left">`
 *   - onClear prop → Renders clear IconButton in `<TextField.Slot side="right">`
 *   - invalid prop → Applies `color="red"` to TextField
 *   - Keep the existing prop API for easy migration
 * - Follow the pattern established by SearchTextField and PasswordTextField
 * - Once created, update imports throughout the codebase to use the new DS-based version
 * - For search inputs, use `SearchTextField` from components/ds
 * - For password inputs, use `PasswordTextField` from components/ds
 *
 * A legacy input component with support for icons, clear button, and custom styling.
 * Provides a wrapper around native HTML input with additional UI features.
 *
 * @param icon - Optional icon to display in the input field
 * @param disabled - Whether the input is disabled
 * @param className - Optional CSS class name (default: 'Form_Input')
 * @param invalid - Whether the input has validation errors
 * @param inputRef - Ref to access the underlying input element
 * @param before - Optional content to display before the input
 * @param onClear - Optional callback for clear button functionality
 * @param autoFocus - Whether the input should automatically focus on mount
 * @param fullWidth - Whether the input should take full width
 * @param defaultValue - Default value for the input
 * @param value - Controlled value for the input
 * @returns The input component with optional icon, clear button, and styling
 */
export default class Input extends React.Component<Props> {
  input: HTMLInputElement | null | undefined = null
  static defaultProps: {
    className: string
  } = {
    className: 'Form_Input',
  }
  focusInput: () => void = () => {
    if (!this.input) return
    this.input.focus()
  }

  render(): React.ReactElement<'div'> {
    const {
      icon,
      className,
      inputRef,
      disabled,
      invalid,
      before,
      onClear,
      autoFocus,
      fullWidth,
      ...rest
    } = this.props
    const inputClass = cn(className, {
      IconInput: !!icon,
      invalid,
      InputContainer_After: before,
      ['Form_Input--full-width']: fullWidth,
    })
    const inputIconClass = cn('InputContainer_Icon', {
      disabled,
    })
    return (
      <div className="InputContainer">
        {icon && (
          <Icon
            className={inputIconClass}
            type="search"
            onClick={this.focusInput}
          />
        )}
        {before && <div className="InputContainer_Before">{before}</div>}
        <input
          autoFocus={autoFocus}
          ref={r => {
            this.input = r

            if (inputRef) {
              if ('current' in inputRef) {
                inputRef.current = r
              } else {
                inputRef(r)
              }
            }
          }}
          {...rest}
          disabled={disabled}
          className={inputClass}
        />
        {onClear && (
          <div className="InputContainer_Clear">
            {<Button onClick={onClear} icon="close" />}
          </div>
        )}
      </div>
    )
  }
}
